import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GearSix, User, Lock, Trash, Calendar, ChartLine, Wallet, CheckCircle, Star, Crown } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import RankBadge from '../components/RankBadge';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

const Section = ({ title, icon: Icon, children }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 mb-6">
    <div className="flex items-center gap-2 mb-5">
      <Icon size={22} weight="duotone" className="text-gold" />
      <h2 className="text-lg font-unbounded font-bold text-white">{title}</h2>
    </div>
    {children}
  </motion.div>
);

const Settings = () => {
  const { user, logout, fetchUser } = useAuth();

  // Fetch full user data on mount to get created_at and total_trades
  useEffect(() => {
    fetchUser();
  }, []);

  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaving, setProfileSaving] = useState(false);

  const [nickname, setNickname] = useState(user?.nickname || '');
  const [nickSaving, setNickSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setNickname(user?.nickname || '');
  }, [user?.nickname]);

  const handleNicknameSave = async () => {
    setNickSaving(true);
    try {
      await api.put('/auth/nickname', { nickname });
      await fetchUser();
      toast.success('Nickname updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update nickname');
    } finally {
      setNickSaving(false);
    }
  };

  const handleProfileUpdate = async () => {
    if (username === user?.username && email === user?.email) {
      toast.info('No changes to save');
      return;
    }
    setProfileSaving(true);
    try {
      const updates = {};
      if (username !== user?.username) updates.username = username;
      if (email !== user?.email) updates.email = email;
      await api.put('/auth/profile', updates);
      await fetchUser();
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      await api.put('/auth/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      await api.delete('/auth/account');
      toast.success('Account deleted');
      logout();
    } catch {
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const createdDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'N/A';

  return (
    <div className="min-h-screen noise-bg" data-testid="settings-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <GearSix size={32} weight="fill" className="text-gold" />
            <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white">Settings</h1>
          </div>
          <p className="text-zinc-400">Manage your account and preferences</p>
        </motion.div>

        {/* Account Info */}
        <Section title="Account Info" icon={ChartLine}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="glass px-4 py-3 rounded-xl">
              <p className="text-xs text-zinc-500 mb-1">Member Since</p>
              <div className="flex items-center gap-1.5">
                <Calendar size={16} className="text-zinc-400" />
                <p className="text-white text-sm font-medium" data-testid="settings-member-since">{createdDate}</p>
              </div>
            </div>
            <div className="glass px-4 py-3 rounded-xl">
              <p className="text-xs text-zinc-500 mb-1">Balance</p>
              <div className="flex items-center gap-1.5">
                <Wallet size={16} className="text-gold" />
                <p className="text-gold text-sm font-mono font-bold" data-testid="settings-balance">${(user?.balance || 0).toLocaleString()}</p>
              </div>
            </div>
            <div className="glass px-4 py-3 rounded-xl">
              <p className="text-xs text-zinc-500 mb-1">Total Trades</p>
              <div className="flex items-center gap-1.5">
                <ChartLine size={16} className="text-zinc-400" />
                <p className="text-white text-sm font-medium" data-testid="settings-total-trades">{user?.total_trades ?? 0}</p>
              </div>
            </div>
          </div>
        </Section>

        {/* Progression */}
        <Section title="Progression" icon={Crown}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <RankBadge rank={user?.rank || 'Beginner'} size="lg" />
              <span className="font-mono font-bold text-gold text-lg" data-testid="settings-xp">{user?.xp || 0} XP</span>
            </div>
            <p className="text-sm text-zinc-400">
              {user?.next_rank
                ? <>Next: <span className="text-white font-bold">{user.next_rank}</span> in {user.xp_to_next_rank} XP</>
                : <span className="text-gold font-bold">🇮🇳 Indian Market Unlocked!</span>}
            </p>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 transition-all"
              style={{ width: `${Math.min(100, Math.round(((user?.xp || 0) / Math.max(1, user?.next_xp_threshold || 500)) * 100))}%` }}
            />
          </div>
          {user?.achievements?.length > 0 ? (
            <div>
              <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wide">Achievements ({user.achievements.length})</p>
              <div className="flex flex-wrap gap-2" data-testid="settings-achievements">
                {user.achievements.map((ach, i) => (
                  <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white text-xs font-medium">
                    {ach}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-zinc-500 italic">Complete a game mode to earn your first achievement!</p>
          )}
        </Section>

        {/* Nickname / Title */}
        <Section title="Display Nickname" icon={Star}>
          <p className="text-xs text-zinc-500 mb-3">Shown on the leaderboard and your public profile. Leave blank to use your username.</p>
          <div className="flex gap-3">
            <Input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="e.g. The Oracle of Omaha"
              maxLength={40}
              className="bg-black/50 border-white/10 text-white"
              data-testid="settings-nickname-input"
            />
            <Button
              onClick={handleNicknameSave}
              disabled={nickSaving || (nickname || '') === (user?.nickname || '')}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold whitespace-nowrap"
              data-testid="settings-save-nickname"
            >
              {nickSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Section>

        {/* Profile */}
        <Section title="Profile" icon={User}>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1.5 block">Username</label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-black/50 border-white/10 text-white"
                data-testid="settings-username-input"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1.5 block">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black/50 border-white/10 text-white"
                data-testid="settings-email-input"
              />
            </div>
            <Button
              onClick={handleProfileUpdate}
              disabled={profileSaving || (username === user?.username && email === user?.email)}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold"
              data-testid="settings-save-profile"
            >
              {profileSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </Section>

        {/* Change Password */}
        <Section title="Change Password" icon={Lock}>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1.5 block">Current Password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="bg-black/50 border-white/10 text-white"
                data-testid="settings-current-password"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1.5 block">New Password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-black/50 border-white/10 text-white"
                data-testid="settings-new-password"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1.5 block">Confirm New Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-black/50 border-white/10 text-white"
                data-testid="settings-confirm-password"
              />
            </div>
            <Button
              onClick={handlePasswordChange}
              disabled={passwordSaving || !currentPassword || !newPassword}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold"
              data-testid="settings-change-password"
            >
              {passwordSaving ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </Section>

        {/* Danger Zone */}
        <Section title="Delete Account" icon={Trash}>
          <p className="text-zinc-400 text-sm mb-4">
            This will permanently delete your account, portfolio, and all transaction history. This action cannot be undone.
          </p>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-sm text-red-400 mb-1.5 block">Type <span className="font-mono font-bold">DELETE</span> to confirm</label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="bg-black/50 border-red-500/30 text-white"
                data-testid="settings-delete-confirm"
              />
            </div>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm !== 'DELETE'}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 font-bold"
              data-testid="settings-delete-account"
            >
              {deleting ? 'Deleting...' : 'Delete Account'}
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default Settings;
